import React, { useState, useRef, useEffect, useMemo } from 'react';
import { ChevronDown, SortAsc, SortDesc, Trash2, Grid, List } from 'lucide-react';
import Button from '@/components/common/Button/Button';
import { useGames } from '@/Context/GameContext';
import styles from './Filters.module.css';

/**
 * Filters Component
 * Provider filter, sort options, and view toggle
 */
const Filters = ({ viewMode, onViewModeChange, showViewToggle = true }) => {
  const {
    selectedVendors,
    sortBy,
    sortOrder,
    vendorDisplayNames,
    searchGames,
    currentQuery,
    setSortBy,
    setSortOrder,
    setSelectedVendors
  } = useGames();

  const [showVendorDropdown, setShowVendorDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [vendorSearchInput, setVendorSearchInput] = useState('');
  const vendorDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);

  // Memoized vendor options
  const vendorOptions = useMemo(() =>
    Object.entries(vendorDisplayNames).map(([key, name]) => ({ key, name })),
    [vendorDisplayNames]
  );

  const filteredVendors = useMemo(() =>
    vendorOptions.filter(vendor =>
      vendor.name.toLowerCase().includes(vendorSearchInput.toLowerCase())
    ),
    [vendorOptions, vendorSearchInput]
  );

  const sortOptions = [
    { key: 'name', label: 'Name' },
    { key: 'vendor', label: 'Provider' },
  ];

  // Handle vendor selection
  const handleVendorToggle = (vendorKey) => {
    const newSelection = selectedVendors.includes(vendorKey)
      ? selectedVendors.filter(v => v !== vendorKey)
      : [...selectedVendors, vendorKey];

    setSelectedVendors(newSelection);
    searchGames(currentQuery, newSelection);
  };

  const clearAllVendors = () => {
    setSelectedVendors([]);
    searchGames(currentQuery, []);
  };

  const selectAllVendors = () => {
    const allKeys = vendorOptions.map(v => v.key);
    setSelectedVendors(allKeys);
    searchGames(currentQuery, allKeys);
  };

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (vendorDropdownRef.current && !vendorDropdownRef.current.contains(event.target)) {
        setShowVendorDropdown(false);
      }
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target)) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={styles.filterContainer}>
      {/* Provider Dropdown */}
      <div className={styles.vendorDropdown} ref={vendorDropdownRef}>
        <Button
          variant={selectedVendors.length > 0 ? 'primary' : 'secondary'}
          size="md"
          onClick={() => setShowVendorDropdown(!showVendorDropdown)}
          className={styles.filterButton}
        >
          <span>
            {selectedVendors.length === 0
              ? 'All Providers'
              : `${selectedVendors.length} Selected`
            }
          </span>
          <ChevronDown size={16} />
        </Button>

        {showVendorDropdown && (
          <div className={styles.dropdownMenu}>
            <div className={styles.vendorSearch}>
              <input
                type="text"
                className={styles.vendorSearchInput}
                placeholder="Search providers..."
                value={vendorSearchInput}
                onChange={(e) => setVendorSearchInput(e.target.value)}
              />
            </div>

            <div className={styles.vendorList}>
              {filteredVendors.map(vendor => (
                <div
                  key={vendor.key}
                  className={styles.vendorItem}
                  onClick={() => handleVendorToggle(vendor.key)}
                >
                  <div className={`${styles.checkbox} ${selectedVendors.includes(vendor.key) ? styles.checkboxChecked : ''}`}>
                    {selectedVendors.includes(vendor.key) && '✓'}
                  </div>
                  <span>{vendor.name}</span>
                </div>
              ))}
            </div>

            <div className={styles.vendorActions}>
              <button className={styles.actionButton} onClick={selectAllVendors}>
                Select All
              </button>
              <button className={styles.actionButton} onClick={clearAllVendors}>
                <Trash2 size={14} />
                Clear
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Sort Dropdown */}
      <div className={styles.sortDropdown} ref={sortDropdownRef}>
        <Button
          variant="secondary"
          size="md"
          onClick={() => setShowSortDropdown(!showSortDropdown)}
          className={styles.filterButton}
        >
          <span>Sort by {sortOptions.find(opt => opt.key === sortBy)?.label}</span>
          {sortOrder === 'asc' ? <SortAsc size={16} /> : <SortDesc size={16} />}
        </Button>

        {showSortDropdown && (
          <div className={styles.dropdownMenu}>
            {sortOptions.map(option => (
              <div
                key={option.key}
                className={`${styles.dropdownItem} ${sortBy === option.key ? styles.dropdownItemActive : ''}`}
                onClick={() => {
                  setSortBy(option.key);
                  setShowSortDropdown(false);
                }}
              >
                {option.label}
                {sortBy === option.key && ' ✓'}
              </div>
            ))}
            <div
              className={styles.dropdownItem}
              onClick={() => {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                setShowSortDropdown(false);
              }}
            >
              {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
            </div>
          </div>
        )}
      </div>

      {/* View Toggle */}
      {showViewToggle && (
        <div className={styles.viewToggle}>
          <Button
            variant={viewMode === 'grid' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => onViewModeChange('grid')}
            title="Grid View"
            className={styles.viewButton}
          >
            <Grid size={16} />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'primary' : 'secondary'}
            size="sm"
            onClick={() => onViewModeChange('list')}
            title="List View"
            className={styles.viewButton}
          >
            <List size={16} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default Filters;
